import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabasePublic } from '../supabaseClient';

const ResetPassword = () => {
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [status, setStatus] = useState('ready'); // ready, processing, success, error
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Check if we have a valid recovery token in the URL and set session so updateUser works
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const accessToken = hashParams.get('access_token');
    const refreshToken = hashParams.get('refresh_token');
    const type = hashParams.get('type');

    console.log('🔑 [RESET] Password reset page loaded', {
      has_token: !!accessToken,
      type: type,
      full_hash: window.location.hash,
    });

    if (!accessToken || type !== 'recovery') {
      console.log('⚠️ [RESET] No valid recovery token found');
      setStatus('error');
      setMessage('Invalid or expired password reset link. Please request a new one.');
      setTimeout(() => {
        navigate('/auth');
      }, 3000);
      return;
    }

    // Establish session from recovery link so updateUser({ password }) works
    if (refreshToken) {
      supabasePublic()
        .auth.setSession({ access_token: accessToken, refresh_token: refreshToken })
        .then(() => console.log('🔑 [RESET] Session set from recovery link'))
        .catch((err) => {
          console.error('❌ [RESET] Failed to set session:', err);
          setStatus('error');
          setMessage('Invalid or expired link. Please request a new password reset.');
        });
    }
  }, [navigate]);

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (!newPassword) {
      setError('Please enter a new password.');
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    setStatus('processing');

    try {
      console.log('🔐 [RESET] Updating password...');

      const { error: updateError } = await supabasePublic().auth.updateUser({
        password: newPassword,
      });

      if (updateError) {
        console.error('❌ [RESET] Error updating password:', updateError);
        setStatus('error');
        setError(updateError.message);
        return;
      }

      console.log('✅ [RESET] Password updated successfully');
      setStatus('success');
      setMessage('Password updated successfully! Redirecting to sign in...');

      // Clear the hash from URL
      window.history.replaceState(null, '', window.location.pathname);

      // Redirect to sign in page after 2 seconds
      setTimeout(() => {
        navigate('/auth');
      }, 2000);
    } catch (err) {
      console.error('💥 [RESET] Exception during password reset:', err);
      setStatus('error');
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '10px',
    borderRadius: '8px',
    border: '1px solid #4A90E2',
    fontSize: '16px',
    boxSizing: 'border-box',
  };

  const labelStyle = {
    fontWeight: 'bold',
    display: 'block',
    marginBottom: '4px',
  };

  const getStatusColor = () => {
    switch (status) {
      case 'success':
        return '#4CAF50';
      case 'error':
        return '#f44336';
      case 'processing':
        return '#2196F3';
      default:
        return '#4A90E2';
    }
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'success':
        return '✅';
      case 'error':
        return '❌';
      case 'processing':
        return '⏳';
      default:
        return '🔑';
    }
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      padding: '20px',
      backgroundColor: '#f5f5f5',
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '40px',
        maxWidth: '500px',
        width: '100%',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      }}>
        <div style={{
          fontSize: '48px',
          marginBottom: '20px',
          textAlign: 'center',
        }}>
          {getStatusIcon()}
        </div>

        <h2 style={{
          color: getStatusColor(),
          marginBottom: '16px',
          fontSize: '24px',
          textAlign: 'center',
        }}>
          {status === 'success' && 'Password Updated!'}
          {status === 'error' && 'Reset Failed'}
          {status === 'processing' && 'Updating Password...'}
          {status === 'ready' && 'Set New Password'}
        </h2>

        {message && (
          <p style={{
            color: getStatusColor(),
            fontSize: '16px',
            lineHeight: '1.5',
            textAlign: 'center',
            marginBottom: '20px',
          }}>
            {message}
          </p>
        )}

        {status === 'error' && error && (
          <>
            <p style={{
              color: '#f44336',
              fontSize: '16px',
              lineHeight: '1.5',
              textAlign: 'center',
              marginBottom: '20px',
            }}>
              {error}
            </p>
            <button
              type="button"
              onClick={() => {
                setStatus('ready');
                setError('');
                setNewPassword('');
                setConfirmPassword('');
              }}
              style={{
                width: '100%',
                backgroundColor: '#3b5998',
                borderRadius: '12px',
                padding: '12px',
                fontWeight: 'bold',
                color: '#fff',
                border: 'none',
                fontSize: '16px',
                cursor: 'pointer',
              }}
            >
              Try again
            </button>
          </>
        )}

        {status === 'ready' && (
          <form onSubmit={handleResetPassword}>
            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>New Password *</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="At least 6 characters"
                style={inputStyle}
                autoFocus
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>Confirm Password *</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter your password"
                style={inputStyle}
              />
            </div>

            {error && (
              <p style={{
                color: 'red',
                marginBottom: '16px',
                fontSize: '14px',
              }}>
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                backgroundColor: '#3b5998',
                borderRadius: '12px',
                padding: '12px',
                fontWeight: 'bold',
                color: '#fff',
                border: 'none',
                fontSize: '16px',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.7 : 1,
              }}
            >
              {loading ? 'Updating...' : 'Update Password'}
            </button>
          </form>
        )}

        {status === 'processing' && (
          <div style={{
            marginTop: '24px',
            display: 'flex',
            justifyContent: 'center',
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: '4px solid #f3f3f3',
              borderTop: '4px solid #2196F3',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }} />
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default ResetPassword;
